'use client';

import { useMutation } from '@tanstack/react-query';
import { PlaybackType } from '@vimond/services/play';
import { postPlayerEvent } from '@vimond/services/player-session';
import { useDebouncedCallback } from 'use-debounce';
import { useAuthentication } from '../../authentication';
import { useSubprofiles } from '../../subprofiles';

type UsePlayerEvents = {
  postPlayerEvent: (params: {
    playback: PlaybackType;
    position: number;
  }) => void;
  postVideoFinishedEvent: (params: {
    playback: PlaybackType;
    position: number;
  }) => void;
  postVideoPausedEvent: (params: {
    playback: PlaybackType;
    position: number;
  }) => void;
};

export const usePlayerEvents = (): UsePlayerEvents => {
  const { jwt } = useAuthentication();
  const { subprofileToken } = useSubprofiles();

  // throttle the mutation so we don't send too many requests during playback
  const throttledPlayerEvents = useDebouncedCallback(
    useMutation({
      mutationFn: async ({
        playback,
        position,
      }: {
        playback: PlaybackType;
        position: number;
      }) => {
        if (!jwt || jwt.length === 0) {
          return;
        }
        if (!subprofileToken || subprofileToken.length === 0) {
          return;
        }
        await postPlayerEvent({ jwt, subprofileToken, position, playback });
      },
    }).mutate,
    30000,
    { maxWait: 30000 },
  );

  // debounce the mutation so we don't send more than one even when playback finishes
  const debouncedPlayerEvents = useDebouncedCallback(
    useMutation({
      mutationFn: async ({
        playback,
        position,
      }: {
        playback: PlaybackType;
        position: number;
      }) => {
        if (!jwt || jwt.length === 0) {
          return;
        }
        if (!subprofileToken || subprofileToken.length === 0) {
          return;
        }
        await postPlayerEvent({ jwt, subprofileToken, position, playback });
      },
    }).mutate,
    1000,
  );

  return {
    postPlayerEvent: throttledPlayerEvents,
    postVideoFinishedEvent: debouncedPlayerEvents,
    postVideoPausedEvent: debouncedPlayerEvents,
  };
};
